/**
 * The controls that say a 词典 headword out loud.
 *
 * A pilot of the entries carries two recordings per reading, a male voice and a
 * female one, made ahead of time and served as ordinary site files. See
 * `docs/audio.md` and `partials/audio-controls.html`.
 *
 * **The controls are hidden in the markup and shown here**, the way the 例句
 * copy buttons are. Playback is JavaScript's, so a visitor without it is shown
 * nothing rather than a control that does nothing when it is pressed.
 *
 * **One `Audio` for the page rather than one per control.** Pressing the other
 * voice, or another reading, while the first is still going should replace it
 * rather than talk over it, and one element gives that for free. It also means
 * one thing to stop when a reader presses the same control twice. A 多音字 has
 * a pair of controls under each of its readings, so there can be eight on a
 * page.
 *
 * **Nothing is preloaded.** A file is about 10 KB, and a page carrying eight of
 * them is 80 KB nobody asked for on every 词典 page a reader passes through
 * without pressing anything. The `src` is set at the moment of the press.
 *
 * **A signed-out reader is asked for an account instead.** Their press plays
 * nothing and puts a line under the buttons offering one. It is the pseudo-gate
 * `download-offer.ts` puts on the vocabulary downloads, and for the same
 * reasons: the files stay public in the bucket, the address stays on the
 * button, and the gate closes on a positive answer and never on silence, so a
 * page whose head script was blocked plays as it always did. `docs/audio.md`
 * under "Playing it asks for an account" has why the audio is gated at all.
 */

import { pointBack } from "./sign-in-link.js";
import {
  listenOffer,
  signUpLinkClass,
  signUpRoute,
} from "../user/sign-up-offer.js";

/** The attribute a control names its recording in. */
const fileAttribute = "data-cb-audio";

/** The attribute set on whichever control is sounding. */
const playingAttribute = "data-cb-playing";

/** The class on the line that offers a signed-out reader an account. */
const offerClass = "audio-offer";

/**
 * What `head/session-state.html` writes on the document for a reader carrying
 * no session. Any other value, or none, plays.
 */
const signedOut = "out";

/**
 * Put the account offer under a row of controls, once however often it is
 * pressed.
 *
 * Drawn here rather than in the markup, so neither renderer changes and no
 * page has to be drawn again for it. The line goes inside the row, where
 * `modules/audio-controls` sends it onto a line of its own under the buttons.
 *
 * `role="status"` because it answers a press. Nothing played, and a screen
 * reader should hear why rather than silence.
 * @param control - The button that was pressed.
 * @param path - The page the reader is on, for the account to come back to.
 */
function offerAccount(control: Element, path: string): void {
  const row = control.parentElement;

  if (row === null || row.querySelector(`.${offerClass}`) !== null) {
    return;
  }

  const offer = control.ownerDocument.createElement("span");
  const link = control.ownerDocument.createElement("a");

  offer.className = offerClass;
  offer.setAttribute("role", "status");
  link.className = signUpLinkClass;
  link.href = signUpRoute;
  link.textContent = listenOffer;
  pointBack(link, path);
  offer.append(link);
  row.append(offer);
}

/** The one player a page plays through, and which control it is sounding for. */
interface PageSound {
  readonly player: HTMLAudioElement;
  sounding: Element | undefined;

  /** Take the mark off whichever control has it. */
  quiet(): void;
}

/** Each page's player, kept across calls so a swap adds controls to it. */
const pageSounds = new WeakMap<Document, PageSound>();

/**
 * The page's player, made on the first call that finds a control.
 *
 * Every control on the page plays through it, the ones a swap brought in as
 * well, so pressing one stops whatever another was saying.
 */
function pageSound(page: Document): PageSound {
  const held = pageSounds.get(page);

  if (held !== undefined) {
    return held;
  }

  const sound: PageSound = {
    player: new Audio(),
    sounding: undefined,
    quiet() {
      sound.sounding?.removeAttribute(playingAttribute);
      sound.sounding = undefined;
    },
  };

  sound.player.addEventListener("ended", () => {
    sound.quiet();
  });

  /*
   * A file that will not load leaves the control unmarked rather than stuck
   * looking busy. The page names its recordings and the bucket holds them, so
   * this is the two disagreeing, which is worth failing quietly on the page and
   * loudly in the generator.
   */
  sound.player.addEventListener("error", () => {
    sound.quiet();
  });
  pageSounds.set(page, sound);

  return sound;
}

/**
 * Wire up the audio controls on a 词典 entry.
 *
 * Does nothing on a page with no controls, which is every page the pilot has
 * not reached. A page in it may still carry controls under one reading and none
 * under another, where the second reading had no character to borrow from.
 *
 * **Only a hidden control is wired**, and wiring shows it, so a second call
 * over the same page passes over what the first wired. `/user/liju/` draws a
 * control beside each recorded sentence of a reader's own and pages by htmx,
 * and `htmx-rebinding.ts` calls this again over the page a swap brings in.
 * The controls it adds play through the player the page already has.
 * @param page - The document to look in.
 * @param path - The page the reader is on, which an account offer returns to.
 * @param within - Where to look, the whole page unless a swap names less.
 */
export function startAudioControls(
  page: Document,
  path: string,
  within: ParentNode = page,
): void {
  const controls = [
    ...within.querySelectorAll(`button[${fileAttribute}][hidden]`),
  ];

  if (controls.length === 0) {
    return;
  }

  const sound = pageSound(page);
  const { player } = sound;
  const quiet = (): void => {
    sound.quiet();
  };

  for (const control of controls) {
    control.removeAttribute("hidden");

    /*
     * Read once at wiring rather than on each press. The selector above is
     * what guarantees it is there, which is also why nothing here checks: a
     * control without the attribute was never in this list.
     */
    // oxlint-disable-next-line typescript/no-non-null-assertion -- the selector is the guarantee
    const file = control.getAttribute(fileAttribute)!;

    control.addEventListener("click", () => {
      // A signed-out reader hears nothing and is offered an account.
      if (page.documentElement.dataset["cbSession"] === signedOut) {
        offerAccount(control, path);
        return;
      }

      /*
       * Pressing the control that is already sounding stops it. A reader who
       * has heard enough should not have to wait out the rest of the word, and
       * a second press is what they will try.
       */
      const wasSounding = sound.sounding === control;

      player.pause();
      quiet();

      if (wasSounding) {
        return;
      }

      player.src = file;
      sound.sounding = control;
      control.setAttribute(playingAttribute, "");

      /*
       * A rejected play leaves nothing marked. Browsers refuse one that no
       * gesture asked for, which cannot happen here, and refuse a file that
       * will not decode, which can.
       */
      player.play().catch(quiet);
    });
  }
}
